"use client"

import * as React from "react"
import { Link, router, usePage } from "@inertiajs/react"
import { Ban, Building2, Crown, DollarSign, LogOut, Palette, Server, Shield, Sparkles, type LucideIcon } from "lucide-react"
import { cn } from "@/lib/utils"
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/shadcn/sidebar"

interface NavItem {
  title: string
  icon: LucideIcon
  url: string
  external?: boolean
}

const navItems: NavItem[] = [
  {
    title: "Accounts",
    icon: Building2,
    url: "/god-mode/accounts",
  },
  {
    title: "Plans",
    icon: Crown,
    url: "/god-mode/plans",
  },
  {
    title: "IP Blocks",
    icon: Ban,
    url: "/god-mode/ip_blocks",
  },
  {
    title: "AI Prompts",
    icon: Sparkles,
    url: "/god-mode/ai-prompts",
  },
  {
    title: "AI Costs",
    icon: DollarSign,
    url: "/god-mode/ai-costs",
  },
  {
    title: "Background Jobs",
    icon: Server,
    url: "/god-mode/sidekiq",
    external: true,
  },
  {
    title: "Design System",
    icon: Palette,
    url: "/design",
  },
]

export function GodModeSidebar(props: React.ComponentProps<typeof Sidebar>) {
  const { url } = usePage()
  const { state } = useSidebar()
  const isCollapsed = state === "collapsed"
  const [activeNav, setActiveNav] = React.useState(() => {
    if (url.includes('/god-mode/plans')) return "Plans"
    if (url.includes('/god-mode/ip_blocks')) return "IP Blocks"
    if (url.includes('/god-mode/ai-prompts')) return "AI Prompts"
    if (url.includes('/god-mode/ai-costs')) return "AI Costs"
    if (url.includes('/god-mode/accounts')) return "Accounts"
    return "Accounts"
  })

  const handleExitGodMode = () => {
    router.delete('/god-mode/exit')
  }

  return (
    <Sidebar
      collapsible="icon"
      className="border-r"
      {...props}
    >
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              onClick={handleExitGodMode}
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground cursor-pointer hover:bg-destructive/10"
            >
              <div className={cn(
                "flex aspect-square size-8 items-center justify-center rounded-lg bg-amber-500/20 text-amber-500",
                isCollapsed && "mx-auto my-2"
              )}>
                <Shield className="size-4" />
              </div>
              {!isCollapsed && (
                <>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold text-amber-500">God Mode</span>
                    <span className="truncate text-xs text-muted-foreground">Super Admin</span>
                  </div>
                  <LogOut className="ml-auto size-4 text-muted-foreground" />
                </>
              )}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent className="px-1.5 md:px-0">
            <SidebarMenu>
              {navItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    tooltip={{
                      children: item.title,
                      hidden: false,
                    }}
                    onClick={() => {
                      setActiveNav(item.title)
                    }}
                    isActive={activeNav === item.title}
                    className="px-2.5 md:px-2 cursor-pointer"
                    asChild
                  >
                    {item.external ? (
                      <a href={item.url} target="_blank" rel="noopener noreferrer">
                        <item.icon />
                        <span>{item.title}</span>
                      </a>
                    ) : (
                      <Link href={item.url}>
                        <item.icon />
                        <span>{item.title}</span>
                      </Link>
                    )}
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  )
}

export default GodModeSidebar
